// My Time Entry — every employee's own time, live from BQE. The web view is a
// week grid (this file); phones get MobileTimeEntry, sharing this state.
// Unsent changes autosave as a draft in j2-tools until "Send to BQE" posts
// them; entries BQE has submitted, approved or billed are read-only.

// DEPENDENCIES
import React, {
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import {
	deleteAPICall,
	getAPICall,
	postAPICall,
	putAPICall,
} from "../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../context/UserContext";

// MUI COMPONENTS
import {
	Alert,
	Button,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Menu,
	MenuItem,
	Popper,
	Snackbar,
	TableSortLabel,
	TextField,
	Tooltip,
	useMediaQuery,
} from "@mui/material";

// MUI ICONS
import AddIcon from "@mui/icons-material/Add";
import BoltIcon from "@mui/icons-material/Bolt";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PestControlRodentIcon from "@mui/icons-material/PestControlRodent";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import UndoIcon from "@mui/icons-material/Undo";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";

// COMPONENTS
import MobileTimeEntry from "./MobileTimeEntry";
import MonthCalendar from "./MonthCalendar";
import QuickAddDialog from "./QuickAddDialog";
import SendDialogBody from "./SendDialogBody";
import FlagCheckboxes from "./TimeEntryFlags";

// DATA / HELPERS
import {
	CELL_STATES,
	TimeEntryDataContext,
	cellState,
	changedCells,
	changedKeySet,
	draftFor,
	isEmptyDraft,
	stableJson,
	postStatus,
	rowsFromEntries,
	sortEditsForRefresh,
	useTimeEntryData,
	useTimeEntryDataStore,
} from "./timeEntryData";
import { useSuggestions } from "./timeEntrySuggestions";
import {
	hoursInputOk,
	addDays,
	fmtUpdatedAt,
	fromIso,
	signInExpired,
	addHours,
	appendMemo,
	buildWeek,
	dayIndexOf,
	emptyCell,
	fmtHours,
	fmtNumber,
	hasEntry,
	isoOf,
	matchesProject,
	mondayOf,
	startOfDay,
	toHours,
	weekLabel,
	weeklyTargetFor,
} from "./timeEntryUtils";

// CSS
import "./TimeEntry.css";

// status: "loading" | "ready" | "error" | "auth" (sign-in expired) while a
// week's BQE entries load. baseline: the entries as loaded from BQE, which
// Send to BQE compares against. loadedAt: when they were loaded.
const EMPTY_WEEK = {
	rows: [],
	keptProjectIds: [],
	baseline: [],
	status: null,
	loadedAt: 0,
	refreshing: false,
};

// A week (or the project list) loaded this long ago refreshes when the page
// is used again; the "Updated" time turns red after STALE_WARN_MS
const STALE_MS = 5 * 60 * 1000;
const STALE_WARN_MS = 30 * 60 * 1000;
// Drafts autosave this long after the last edit; a failed save retries later
const DRAFT_SAVE_MS = 1000;
const DRAFT_RETRY_MS = 15 * 1000;

const rowHours = (row) => row.cells.reduce((a, c) => a + toHours(c.hours), 0);
// Same BQE project link the PM dashboard uses
const bqeProjectUrl = (id) =>
	`https://wap1-01.bqecore.com/webapp/Project/Edit/${encodeURIComponent(id)}`;

const projectViewerUrl = (id) =>
	`/projects/viewer?recordId=${encodeURIComponent(id)}`;

// Cell colors explained in the footer, in the order time moves through them
const CELL_LEGEND = [
	"unsent",
	"unsaved",
	"removed",
	"sent",
	"submitted",
	"approved",
	"billed",
];

// Why unsent cells aren't saved in Tools yet (the week's draft status)
const DRAFT_TIPS = {
	saving: "Not sent to BQE. Saving to Tools as a draft…",
	error: "Not sent to BQE, and the draft didn't save to Tools. It will try again.",
	conflict:
		"Not sent to BQE, and the draft didn't save to Tools: a newer one was saved on another device.",
};

const needsMemo = (cell) => toHours(cell.hours) > 0 && !cell.memo.trim();

// New activity lines go directly under the project's existing lines
const insertAfterProject = (rows, row) => {
	const last = rows.reduce(
		(acc, r, k) => (r.projectId === row.projectId ? k : acc),
		-1,
	);
	return last === -1
		? [...rows, row]
		: [...rows.slice(0, last + 1), row, ...rows.slice(last + 1)];
};

// `days` records which days a line was added on, so the mobile day view can
// show it before any hours are entered
const newRow = (id, projectId, activityId, days, billable) => ({
	id,
	projectId,
	activityId,
	days,
	cells: Array.from({ length: 7 }, () => emptyCell(billable)),
});

// Billed / in-approval BQE entries can't be changed here
const rowLocked = (row) => row.cells.some((c) => c.lockReason);

// FUNCTIONAL COMPONENT
export default function TimeEntry({
	hasProjectViewer = false,
	isAdmin = false,
}) {
	// MSAL
	const { instance, accounts } = useMsal();

	// CONTEXT
	const employeeID = useContext(UserContext);

	// Same breakpoint App.js uses for its mobile top bar and nav drawer
	const isMobile = useMediaQuery("(max-width: 767px)");

	// DATA (employee, projects, activities)
	const data = useTimeEntryDataStore(instance, accounts[0]);
	const {
		employee,
		projects,
		projectsState,
		loadBasics,
		refreshBasicsIfStale,
		projectOf,
		activityOf,
		registerEntries,
		ensureActivities,
		activitiesState,
		activitiesFor,
		recentProjectIds,
		billableDefault,
	} = data;

	const today = useMemo(() => startOfDay(new Date()), []);
	const targetSource = { hours_per_week: employee && employee.hoursPerWeek };
	const weeklyTarget = weeklyTargetFor(targetSource);

	// STATES
	const [weeks, setWeeks] = useState({});
	const [weekKey, setWeekKey] = useState(() => isoOf(mondayOf(today)));
	const [selected, setSelected] = useState(null); // { rowId, day }
	const [collapsed, setCollapsed] = useState({});
	const [addingProject, setAddingProject] = useState(null); // { query }
	const [activityMenu, setActivityMenu] = useState(null); // { projectId, anchorEl }
	const [confirm, setConfirm] = useState(null); // { kind: "row" | "project", id }
	const [weekCalAnchor, setWeekCalAnchor] = useState(null);
	const [quickOpen, setQuickOpen] = useState(false);
	const [quickDate, setQuickDate] = useState(today);
	const [flashProjectId, setFlashProjectId] = useState(null);
	// Project name sort for the grid: null (entry order), "asc" or "desc".
	// Not saved — every visit starts in entry order.
	const [projectSort, setProjectSort] = useState(null);
	const [snack, setSnack] = useState(null); // { severity, text }
	const [scrollerWidth, setScrollerWidth] = useState(0);
	// Send to BQE: { phase: "posting" | "done", scope, cells, results, error }
	const [posting, setPosting] = useState(null);
	// Re-renders once a minute so the "Updated" time can turn stale
	const [, setClock] = useState(0);
	// Draft autosave per week: "saving" | "saved" | "error" | "conflict"
	const [draftSave, setDraftSave] = useState({});

	// REFS
	const nextRowId = useRef(1);
	// Per week: { version, savedJson, busy, timer, retryAt, conflict }
	const drafts = useRef({});
	const scrollerRef = useRef(null);
	const groupRefs = useRef({});
	const pendingFocus = useRef(null);
	const pendingScroll = useRef(null);
	const flashTimer = useRef(null);

	const week = useMemo(() => buildWeek(weekKey, today), [weekKey, today]);
	// Suggested entries (Dialpad calls), loaded when Quick Add opens on the web
	const suggestions = useSuggestions(
		instance,
		accounts[0],
		weekKey,
		quickOpen && !isMobile,
	);
	const current = weeks[weekKey] || EMPTY_WEEK;
	const rows = current.rows;
	const todayIndex = week.findIndex((d) => d.isToday);

	const updateWeek = useCallback((key, fn) => {
		setWeeks((w) => {
			const wk = w[key] || EMPTY_WEEK;
			return { ...w, [key]: { ...wk, ...fn(wk) } };
		});
	}, []);

	const setCell = (rowId, day, patch) =>
		updateWeek(weekKey, (wk) => ({
			rows: wk.rows.map((r) =>
				r.id !== rowId
					? r
					: {
							...r,
							cells: r.cells.map((c, i) =>
								i === day ? { ...c, ...patch } : c,
							),
						},
			),
		}));

	// LOADERS
	// A week's entries come live from BQE and are grouped into grid rows
	const fetchWeek = async (key) => {
		const res = await getAPICall(
			instance,
			accounts[0],
			"/api/time-entry/week",
			{ start: key },
		);
		const entries = res.data.entries || [];
		registerEntries(entries);
		return entries;
	};

	// Grid rows for fresh BQE entries, with `keep` (unsent edits) put back on
	// top. prevRows lends its row ids (so an open line stays open) and its
	// empty lines (added, no hours yet).
	const buildRows = (key, entries, keep = [], prevRows = []) => {
		const days = buildWeek(key, today);
		const built = rowsFromEntries(entries, days, nextRowId.current);
		nextRowId.current = built.nextId;
		const sameLine = (a, b) =>
			a.projectId === b.projectId && a.activityId === b.activityId;
		let rowsNow = built.rows.map((r) => {
			const prev = prevRows.find((x) => sameLine(x, r));
			return prev ? { ...r, id: prev.id, days: prev.days } : r;
		});
		prevRows
			.filter(
				(x) =>
					!x.cells.some(hasEntry) &&
					!rowsNow.some((r) => sameLine(r, x)),
			)
			.forEach((x) => (rowsNow = insertAfterProject(rowsNow, x)));
		keep.forEach((c) => {
			const day = days.findIndex((d) => d.iso === c.date);
			let row = rowsNow.find((r) => sameLine(r, c));
			if (!row) {
				if (c.hours <= 0) return;
				row = newRow(
					nextRowId.current++,
					c.projectId,
					c.activityId,
					[day],
					c.billable,
				);
				rowsNow = insertAfterProject(rowsNow, row);
			}
			rowsNow = rowsNow.map((r) =>
				r.id !== row.id
					? r
					: {
							...r,
							cells: r.cells.map((cell, i) =>
								i !== day
									? cell
									: {
											...cell,
											hours: c.hours
												? String(c.hours)
												: "",
											memo: c.hours ? c.memo : "",
											billable: c.billable,
											compTime: c.compTime,
										},
							),
						},
			);
		});
		// Lines whose entries were all removed (a kept deletion) leave the grid
		return rowsNow.filter(
			(r) =>
				r.cells.some(hasEntry) ||
				(r.days || []).length > 0 ||
				!built.rows.some((b) => sameLine(b, r)),
		);
	};

	// The week's saved draft (unsent changes); a failed read doesn't stop the
	// week loading, and the next save then checks for a newer draft
	const fetchDraft = async (key) => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/time-entry/draft",
				{ start: key },
			);
			return res.data;
		} catch (error) {
			console.error("TimeEntry - fetchDraft - error:", error);
			return { data: null, version: 0 };
		}
	};

	// Full load (shows the loading notice): BQE's entries with the saved draft
	// on top. `keep`: cells that failed to post. quiet: skip the notice about
	// draft edits BQE replaced (after a send, those are the ones that saved).
	const loadWeek = async (key, keep = [], { quiet = false } = {}) => {
		setWeeks((w) => ({
			...w,
			[key]: { ...EMPTY_WEEK, status: "loading" },
		}));
		try {
			const [entries, draft] = await Promise.all([
				fetchWeek(key),
				fetchDraft(key),
			]);
			const saved = draft.data;
			const fromDraft = saved
				? sortEditsForRefresh(saved.cells, entries)
				: { keep: [], dropped: [] };
			const cellId = (c) =>
				c.projectId + "|" + c.activityId + "|" + c.date;
			const allKeep = [
				...keep,
				...fromDraft.keep.filter(
					(c) => !keep.some((k) => cellId(k) === cellId(c)),
				),
			];
			// Lines added without hours come back as empty rows
			const draftLines = saved
				? saved.lines.map((l) =>
						newRow(
							nextRowId.current++,
							l.projectId,
							l.activityId,
							l.days,
							billableDefault(l.projectId, l.activityId),
						),
					)
				: [];
			const rowsNow = buildRows(key, entries, allKeep, draftLines);
			// Names for draft lines BQE has no entries for yet
			new Set([
				...allKeep.map((c) => c.projectId),
				...draftLines.map((r) => r.projectId),
			]).forEach((projectId) => ensureActivities(projectId));
			drafts.current[key] = {
				version: draft.version || 0,
				savedJson: saved ? stableJson(saved) : null,
			};
			setDraftSave((d) => ({ ...d, [key]: saved ? "saved" : null }));
			setWeeks((w) => ({
				...w,
				[key]: {
					...EMPTY_WEEK,
					rows: rowsNow,
					keptProjectIds: saved ? saved.keptProjectIds : [],
					baseline: entries,
					status: "ready",
					loadedAt: Date.now(),
				},
			}));
			if (fromDraft.dropped.length && !quiet) {
				setSnack({
					severity: "warning",
					text:
						(fromDraft.dropped.length === 1
							? "1 saved draft edit was"
							: fromDraft.dropped.length +
								" saved draft edits were") +
						" replaced because the entry changed in BQE.",
				});
			}
		} catch (error) {
			console.error("TimeEntry - loadWeek - error:", error);
			setWeeks((w) => ({
				...w,
				[key]: {
					...EMPTY_WEEK,
					status: signInExpired(error) ? "auth" : "error",
				},
			}));
		}
	};

	// Background refresh: the week stays on screen and unsent edits are kept,
	// except where BQE changed or locked the same day and activity (BQE wins)
	const refreshWeek = async (key) => {
		const wk = weeksRef.current[key];
		if (!wk || wk.status !== "ready" || wk.refreshing) return;
		if (postingRef.current && postingRef.current.phase !== "done") return;
		updateWeek(key, () => ({ refreshing: true }));
		let entries;
		try {
			entries = await fetchWeek(key);
		} catch (error) {
			console.error("TimeEntry - refreshWeek - error:", error);
			updateWeek(key, () => ({ refreshing: false }));
			setSnack(
				signInExpired(error)
					? {
							severity: "warning",
							text: "Your sign-in expired. Reload the page to sign in again.",
						}
					: {
							severity: "warning",
							text: "Couldn't refresh from BQE. Showing what was loaded earlier.",
						},
			);
			return;
		}
		// Read the week again now, so edits made while it loaded are kept too
		const now = weeksRef.current[key];
		const days = buildWeek(key, today);
		const edits = changedCells(now.rows, now.baseline, days);
		const { keep, dropped } = sortEditsForRefresh(edits, entries);
		const rowsNow = buildRows(key, entries, keep, now.rows);
		setWeeks((w) => ({
			...w,
			[key]: {
				...w[key],
				rows: rowsNow,
				baseline: entries,
				loadedAt: Date.now(),
				refreshing: false,
			},
		}));
		if (dropped.length) {
			setSnack({
				severity: "warning",
				text:
					(dropped.length === 1
						? "1 unsent edit was"
						: dropped.length + " unsent edits were") +
					" replaced because the entry changed in BQE.",
			});
		}
	};

	// DRAFTS
	// The week's draft as the JSON that would be saved (null: nothing unsent)
	const draftJsonFor = (key) => {
		const wk = weeksRef.current[key];
		const payload = draftFor(wk, buildWeek(key, today));
		return isEmptyDraft(payload) ? null : stableJson(payload);
	};

	// Saves (or deletes, when nothing's left unsent) a week's draft. force:
	// overwrite a newer draft from another device.
	const saveDraft = async (key, force = false) => {
		const wk = weeksRef.current[key];
		const d = drafts.current[key];
		if (!wk || wk.status !== "ready" || !d || d.busy) return;
		const json = draftJsonFor(key);
		if (json === d.savedJson && !force) return;
		d.busy = true;
		d.timer = null;
		setDraftSave((s) => ({ ...s, [key]: "saving" }));
		try {
			if (json === null) {
				if (d.version) {
					await deleteAPICall(
						instance,
						accounts[0],
						"/api/time-entry/draft",
						{ start: key },
					);
				}
				d.version = 0;
			} else {
				const res = await putAPICall(
					instance,
					accounts[0],
					"/api/time-entry/draft",
					{
						start: key,
						data: JSON.parse(json),
						version: d.version,
						force,
					},
				);
				d.version = res.data.version;
			}
			d.savedJson = json;
			d.retryAt = 0;
			d.conflict = null;
			setDraftSave((s) => ({
				...s,
				[key]: json === null ? null : "saved",
			}));
		} catch (error) {
			console.error("TimeEntry - saveDraft - error:", error);
			if (error.response && error.response.status === 409) {
				// Another device saved a newer draft: pause autosave and ask
				d.conflict = true;
				setDraftSave((s) => ({ ...s, [key]: "conflict" }));
				setSnack({
					severity: "warning",
					text: "A newer draft of this week was saved on another device.",
					actions: [
						{
							label: "Load it",
							onClick: () => {
								drafts.current[key] = {
									version: 0,
									savedJson: null,
								};
								loadWeek(key);
							},
						},
						{
							label: "Keep mine",
							onClick: () => {
								d.conflict = null;
								saveDraft(key, true);
							},
						},
					],
				});
			} else {
				d.retryAt = Date.now() + DRAFT_RETRY_MS;
				setDraftSave((s) => ({ ...s, [key]: "error" }));
			}
		} finally {
			d.busy = false;
		}
	};
	const saveDraftRef = useRef(saveDraft);
	saveDraftRef.current = saveDraft;

	// Latest values for the window listeners below
	const weeksRef = useRef(weeks);
	weeksRef.current = weeks;
	const weekKeyRef = useRef(weekKey);
	weekKeyRef.current = weekKey;
	const postingRef = useRef(posting);
	postingRef.current = posting;
	const refreshRef = useRef(refreshWeek);
	refreshRef.current = refreshWeek;

	const isStale = (wk) =>
		wk && wk.status === "ready" && Date.now() - wk.loadedAt >= STALE_MS;

	// USE EFFECTS
	useEffect(() => {
		loadBasics();
	}, [loadBasics]);

	// Load a week the first time it's shown; refresh it on a later visit if
	// it's been a while
	useEffect(() => {
		if (!weeks[weekKey]) loadWeek(weekKey);
		else if (isStale(weeks[weekKey])) refreshWeek(weekKey);
	}, [weekKey]); // eslint-disable-line react-hooks/exhaustive-deps

	// Autosave: a week's draft saves shortly after its last edit
	useEffect(() => {
		Object.keys(weeks).forEach((key) => {
			if (weeks[key].status !== "ready") return;
			const d = drafts.current[key];
			if (!d || d.busy || d.conflict) return;
			if (draftJsonFor(key) === d.savedJson) {
				clearTimeout(d.timer);
				d.timer = null;
				return;
			}
			clearTimeout(d.timer);
			d.timer = setTimeout(
				() => saveDraftRef.current(key),
				Math.max(DRAFT_SAVE_MS, (d.retryAt || 0) - Date.now()),
			);
		});
	}, [weeks, draftSave]); // eslint-disable-line react-hooks/exhaustive-deps

	// Warn before leaving while a draft hasn't finished saving
	useEffect(() => {
		const warn = (e) => {
			const unsaved = Object.keys(weeksRef.current).some((key) => {
				const d = drafts.current[key];
				return (
					weeksRef.current[key].status === "ready" &&
					d &&
					(d.busy || draftJsonFor(key) !== d.savedJson)
				);
			});
			if (unsaved) {
				e.preventDefault();
				e.returnValue = "";
			}
		};
		window.addEventListener("beforeunload", warn);
		return () => window.removeEventListener("beforeunload", warn);
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Coming back to the page after 5+ minutes refreshes the week on screen
	// (and the project list after an hour)
	useEffect(() => {
		const onReturn = () => {
			if (document.visibilityState !== "visible") return;
			if (isStale(weeksRef.current[weekKeyRef.current])) {
				refreshRef.current(weekKeyRef.current);
			}
			refreshBasicsIfStale();
		};
		document.addEventListener("visibilitychange", onReturn);
		window.addEventListener("focus", onReturn);
		const tick = setInterval(() => setClock((c) => c + 1), 60 * 1000);
		return () => {
			document.removeEventListener("visibilitychange", onReturn);
			window.removeEventListener("focus", onReturn);
			clearInterval(tick);
		};
	}, [refreshBasicsIfStale]);

	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "My Time Entry",
			employeeID: employeeID,
			notes: "Accessed the My Time Entry page",
		}).catch(() => {});
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Detail strips and empty rows stick to the left edge at the scroller's
	// visible width
	useLayoutEffect(() => {
		const el = scrollerRef.current;
		if (!el) return;
		// Edge shadows on the pinned first/last columns while there's more
		// table scrolled out of view on that side
		const syncEdges = () => {
			el.classList.toggle("teScroller--scrolledX", el.scrollLeft > 0);
			el.classList.toggle(
				"teScroller--moreRight",
				el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
			);
		};
		const sync = () => {
			setScrollerWidth(el.clientWidth);
			syncEdges();
		};
		sync();
		const ro = new ResizeObserver(sync);
		ro.observe(el);
		el.addEventListener("scroll", syncEdges, { passive: true });
		return () => {
			ro.disconnect();
			el.removeEventListener("scroll", syncEdges);
		};
	}, []);

	useEffect(() => {
		if (pendingFocus.current) {
			const el = document.querySelector(
				`[data-te-cell="${pendingFocus.current}"]`,
			);
			pendingFocus.current = null;
			if (el) el.focus();
		}
		if (pendingScroll.current != null) {
			const el = groupRefs.current[pendingScroll.current];
			const sc = scrollerRef.current;
			pendingScroll.current = null;
			if (el && sc) {
				const top =
					el.getBoundingClientRect().top -
					sc.getBoundingClientRect().top +
					sc.scrollTop -
					44;
				sc.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
			}
		}
	});

	// Q opens Quick Add from anywhere on the page except text fields
	useEffect(() => {
		const onKey = (e) => {
			const tag = (e.target && e.target.tagName) || "";
			if (
				(e.key === "q" || e.key === "Q") &&
				!quickOpen &&
				!/INPUT|TEXTAREA|SELECT/.test(tag) &&
				!e.target.isContentEditable &&
				!e.metaKey &&
				!e.ctrlKey &&
				!e.altKey
			) {
				e.preventDefault();
				openQuick();
			}
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	});

	// Clicking outside the open memo strip closes it. Hour cells are exempt
	// (focusing one moves the strip there) as are MUI portals — dialogs,
	// menus, popovers and the snackbar opened from the strip or toolbar.
	useEffect(() => {
		if (!selected) return;
		const onMouseDown = (e) => {
			if (
				e.target.closest(
					".teDetail, .teCell, .teM-activity, .MuiModal-root, .MuiSnackbar-root",
				)
			) {
				return;
			}
			setSelected(null);
		};
		document.addEventListener("mousedown", onMouseDown);
		return () => document.removeEventListener("mousedown", onMouseDown);
	}, [selected]);

	// Clicking outside the "Add a project" search (and its list, which renders
	// in a portal) closes it, same as Cancel
	useEffect(() => {
		if (!addingProject) return;
		const onMouseDown = (e) => {
			if (!e.target.closest(".teAddStrip, .teAddStrip-popper")) {
				setAddingProject(null);
			}
		};
		document.addEventListener("mousedown", onMouseDown);
		return () => document.removeEventListener("mousedown", onMouseDown);
	}, [addingProject]);

	useEffect(() => () => clearTimeout(flashTimer.current), []);

	// NAVIGATION
	const goToDate = (date) => {
		const key = isoOf(mondayOf(date));
		setQuickDate(startOfDay(date));
		if (key !== weekKey) {
			setWeekKey(key);
			setSelected(null);
			setAddingProject(null);
		}
	};

	const hoursOnDate = (date) => {
		const wk = weeks[isoOf(mondayOf(date))];
		if (!wk) return 0;
		const i = dayIndexOf(date);
		return wk.rows.reduce((a, r) => a + toHours(r.cells[i].hours), 0);
	};

	// Hours on one project on a day, including changes not yet sent
	const projectHoursOnDate = (projectId, date) => {
		const wk = weeks[isoOf(mondayOf(date))];
		if (!wk) return 0;
		const i = dayIndexOf(date);
		return wk.rows
			.filter((r) => r.projectId === projectId)
			.reduce((a, r) => a + toHours(r.cells[i].hours), 0);
	};

	const openQuick = () => {
		setSelected(null);
		setQuickDate((d) =>
			isoOf(mondayOf(d)) === weekKey
				? d
				: week[Math.max(0, todayIndex)].date,
		);
		setQuickOpen(true);
	};

	// ROW / PROJECT HANDLERS
	// Opens the new line on today's column (web) or the given day (mobile)
	const addActivity = (
		projectId,
		activityId,
		day = Math.max(0, todayIndex),
	) => {
		// Re-adding an activity that's already on the week (e.g. from another
		// day on mobile) reuses its line instead of duplicating it
		const existing = rows.find(
			(r) => r.projectId === projectId && r.activityId === activityId,
		);
		const id = existing ? existing.id : nextRowId.current++;
		updateWeek(weekKey, (wk) => ({
			rows: existing
				? wk.rows.map((r) =>
						r.id !== id
							? r
							: {
									...r,
									days: [
										...new Set([...(r.days || []), day]),
									],
								},
					)
				: insertAfterProject(
						wk.rows,
						newRow(
							id,
							projectId,
							activityId,
							[day],
							billableDefault(projectId, activityId),
						),
					),
		}));
		setCollapsed((c) => ({ ...c, [projectId]: false }));
		setSelected({ rowId: id, day });
		setActivityMenu(null);
		pendingFocus.current = id + "-" + day;
	};

	// Picking from "Add a project": add it to the week with no activity lines
	// (the user picks those with +), then scroll to and flash it
	const landOnProject = (projectId) => {
		const onWeek =
			rows.some((r) => r.projectId === projectId) ||
			current.keptProjectIds.includes(projectId);
		if (!onWeek) {
			updateWeek(weekKey, (wk) => ({
				keptProjectIds: [...wk.keptProjectIds, projectId],
			}));
		}
		setAddingProject(null);
		setCollapsed((c) => ({ ...c, [projectId]: false }));
		setFlashProjectId(projectId);
		pendingScroll.current = projectId;
		clearTimeout(flashTimer.current);
		flashTimer.current = setTimeout(() => setFlashProjectId(null), 1600);
	};

	// Removing a project's last activity keeps the (empty) project on the week
	const removeRow = (rowId) => {
		updateWeek(weekKey, (wk) => {
			const row = wk.rows.find((r) => r.id === rowId);
			const remaining = wk.rows.filter((r) => r.id !== rowId);
			const keep =
				row &&
				!remaining.some((r) => r.projectId === row.projectId) &&
				!wk.keptProjectIds.includes(row.projectId);
			return {
				rows: remaining,
				keptProjectIds: keep
					? [...wk.keptProjectIds, row.projectId]
					: wk.keptProjectIds,
			};
		});
		if (selected && selected.rowId === rowId) setSelected(null);
		setConfirm(null);
	};

	const removeProject = (projectId) => {
		updateWeek(weekKey, (wk) => ({
			rows: wk.rows.filter((r) => r.projectId !== projectId),
			keptProjectIds: wk.keptProjectIds.filter((p) => p !== projectId),
		}));
		setSelected(null);
		setConfirm(null);
	};

	// Mobile "Remove": deletes a line from one day (hours, memo, flags). The
	// line leaves the week once no other day uses it; the project stays.
	const removeDayEntry = (rowId, day) => {
		updateWeek(weekKey, (wk) => {
			const row = wk.rows.find((r) => r.id === rowId);
			if (!row) return {};
			const cleared = {
				...row,
				days: (row.days || []).filter((x) => x !== day),
				cells: row.cells.map((c, i) =>
					i === day
						? emptyCell(
								billableDefault(row.projectId, row.activityId),
							)
						: c,
				),
			};
			const stillUsed =
				cleared.days.length > 0 || cleared.cells.some(hasEntry);
			const remaining = stillUsed
				? wk.rows.map((r) => (r.id === rowId ? cleared : r))
				: wk.rows.filter((r) => r.id !== rowId);
			const keep =
				!remaining.some((r) => r.projectId === row.projectId) &&
				!wk.keptProjectIds.includes(row.projectId);
			return {
				rows: remaining,
				keptProjectIds: keep
					? [...wk.keptProjectIds, row.projectId]
					: wk.keptProjectIds,
			};
		});
		setSelected(null);
		setConfirm(null);
	};

	// kind: "row" (whole line), "project", or "day" (one day's entry, mobile)
	const askRemove = (kind, id, day) => {
		const targetRows =
			kind === "project"
				? rows.filter((r) => r.projectId === id)
				: rows.filter((r) => r.id === id);
		const locked =
			kind === "day"
				? targetRows.some((r) => r.cells[day].lockReason)
				: targetRows.some(rowLocked);
		if (locked) {
			setSnack({
				severity: "warning",
				text: "This includes time that's already submitted, approved or billed in BQE, so it can't be removed here.",
			});
			return;
		}
		let hrs;
		if (kind === "day") {
			const r = rows.find((x) => x.id === id);
			hrs = r ? toHours(r.cells[day].hours) : 0;
		} else {
			const affected =
				kind === "row"
					? rows.filter((r) => r.id === id)
					: rows.filter((r) => r.projectId === id);
			hrs = affected.reduce((a, r) => a + rowHours(r), 0);
		}
		if (hrs > 0) setConfirm({ kind, id, day });
		else if (kind === "day") removeDayEntry(id, day);
		else if (kind === "row") removeRow(id);
		else removeProject(id);
	};

	// QUICK ADD HANDLERS
	// Hours and memo add onto an existing line for that activity and day
	// The cell Quick Add would add to, or null if there's no line for it yet
	const existingCell = (projectId, activityId, date) => {
		const wk = weeks[isoOf(mondayOf(date))];
		const row =
			wk &&
			wk.rows.find(
				(r) => r.projectId === projectId && r.activityId === activityId,
			);
		return row ? row.cells[dayIndexOf(date)] : null;
	};

	// Returns false (and explains) when the entry can't be added
	const quickSave = ({
		projectId,
		activityId,
		hours,
		memo,
		date,
		billable,
		compTime,
	}) => {
		const key = isoOf(mondayOf(date));
		const day = dayIndexOf(date);
		const wkNow = weeks[key] || EMPTY_WEEK;
		if (wkNow.status !== "ready") {
			setSnack({
				severity: "info",
				text: "That week is still loading from BQE. Try again in a moment.",
			});
			return false;
		}
		const match = wkNow.rows.find(
			(r) => r.projectId === projectId && r.activityId === activityId,
		);
		if (match && match.cells[day].lockReason) {
			setSnack({
				severity: "warning",
				text: "That entry is already submitted, approved or billed in BQE, so hours can't be added to it here.",
			});
			return false;
		}
		const exists = Boolean(match);
		const id = exists ? null : nextRowId.current++;
		updateWeek(key, (wk) => {
			let list = wk.rows;
			let row = list.find(
				(r) => r.projectId === projectId && r.activityId === activityId,
			);
			if (!row) {
				row = newRow(
					id,
					projectId,
					activityId,
					[day],
					billableDefault(projectId, activityId),
				);
				list = insertAfterProject(list, row);
			}
			return {
				rows: list.map((r) =>
					r.id !== row.id
						? r
						: {
								...r,
								cells: r.cells.map((c, i) =>
									i !== day
										? c
										: {
												...c,
												hours: addHours(c.hours, hours),
												memo: appendMemo(c.memo, memo),
												billable,
												compTime,
											},
								),
							},
				),
				keptProjectIds: wk.keptProjectIds.filter(
					(p) => p !== projectId,
				),
			};
		});
	};

	// REVERT
	// Puts back what's in BQE (added lines and projects go too): the week on
	// screen on web, every unsent change on mobile (allWeeks). Undo restores.
	const revertChanges = ({ allWeeks = false } = {}) => {
		const keys = weekKeysFor(allWeeks);
		const before = {};
		keys.forEach((key) => {
			const wk = weeksRef.current[key];
			if (!wk || wk.status !== "ready") return;
			before[key] = { rows: wk.rows, keptProjectIds: wk.keptProjectIds };
			const rowsNow = buildRows(key, wk.baseline);
			updateWeek(key, () => ({ rows: rowsNow, keptProjectIds: [] }));
		});
		setSelected(null);
		setSnack({
			severity: "info",
			text: "Changes reverted.",
			actions: [
				{
					label: "Undo",
					onClick: () =>
						Object.keys(before).forEach((key) =>
							updateWeek(key, () => before[key]),
						),
				},
			],
		});
	};

	// SUPER SECRET IT TIME ENTRY MODE (My Time Entry admins only)
	// Tops each day up to 8 hrs with System Improvements on J2 Consulting
	// Overhead | Admin: Mon–Fri on web, the day on screen on mobile. Nothing
	// is overwritten: the missing hours are added onto that line (its memo
	// appended), days already at 8+ hrs are skipped, and so are days whose
	// System Improvements entry is submitted, approved or billed in BQE.
	const IT_MEMO = "Performed system improvements";
	const itTimeEntryMode = async (onlyDate) => {
		if (current.status !== "ready") return;
		const project = projects.find(
			(p) => p.overhead && /\|\s*Admin$/.test(p.name),
		);
		const activity =
			project &&
			(await ensureActivities(project.id)).find(
				(a) => a.name.toLowerCase() === "system improvements",
			);
		if (!activity) {
			setSnack({
				severity: "warning",
				text: "Couldn't find System Improvements on J2 Consulting Overhead.",
			});
			return;
		}
		const days = onlyDate
			? [week.findIndex((d) => d.iso === onlyDate)]
			: [0, 1, 2, 3, 4];
		const isLine = (r) =>
			r.projectId === project.id && r.activityId === activity.id;
		const line = rows.find(isLine);

		// Hours to add per day (to reach 8 across all lines)
		const add = {};
		days.forEach((i) => {
			const total = rows.reduce(
				(a, r) => a + toHours(r.cells[i].hours),
				0,
			);
			const need = Math.round((8 - total) * 10) / 10;
			if (need > 0 && !(line && line.cells[i].lockReason)) add[i] = need;
		});
		const fillDays = Object.keys(add).map(Number);
		if (!fillDays.length) {
			setSnack({
				severity: "info",
				text: onlyDate
					? "This day already has 8+ hrs, so nothing was added."
					: "Every weekday already has 8+ hrs, so nothing was added.",
			});
			return;
		}

		updateWeek(weekKey, (wk) => {
			let list = wk.rows;
			let row = list.find(isLine);
			if (!row) {
				row = newRow(
					nextRowId.current++,
					project.id,
					activity.id,
					fillDays,
					false,
				);
				list = insertAfterProject(list, row);
			}
			return {
				rows: list.map((r) =>
					r.id !== row.id
						? r
						: {
								...r,
								cells: r.cells.map((c, i) =>
									add[i] === undefined
										? c
										: {
												...c,
												hours: addHours(
													c.hours,
													add[i],
												),
												memo: c.memo.includes(IT_MEMO)
													? c.memo
													: appendMemo(
															c.memo,
															IT_MEMO,
														),
											},
								),
							},
				),
				keptProjectIds: wk.keptProjectIds.filter(
					(p) => p !== project.id,
				),
			};
		});
		setCollapsed((c) => ({ ...c, [project.id]: false }));
		const total = fillDays.reduce((a, i) => a + add[i], 0);
		setSnack({
			severity: "success",
			text:
				"Super Secret IT Time Entry Mode: added " +
				fmtHours(total) +
				" hrs of System Improvements (" +
				fillDays
					.map((i) => week[i].name + " " + fmtHours(add[i]))
					.join(", ") +
				"). Send to BQE when ready.",
		});
	};

	// SEND TO BQE
	// Web sends the week on screen; mobile (allWeeks) sends every change made,
	// whatever the day or week
	const weekKeysFor = (allWeeks) =>
		allWeeks
			? Object.keys(weeksRef.current)
					.filter((k) => weeksRef.current[k].status === "ready")
					.sort()
			: [weekKey];

	const sendTime = async ({ allWeeks = false } = {}) => {
		const keys = weekKeysFor(allWeeks);
		// Every entry needs a memo first: jump to the first one missing
		for (const key of keys) {
			const wk = weeksRef.current[key];
			for (const r of wk.rows) {
				const day = r.cells.findIndex(needsMemo);
				if (day < 0) continue;
				const missing = keys.reduce(
					(n, k) =>
						n +
						weeksRef.current[k].rows.reduce(
							(a, x) => a + x.cells.filter(needsMemo).length,
							0,
						),
					0,
				);
				if (key !== weekKey) goToDate(buildWeek(key, today)[day].date);
				else if (allWeeks) goToDate(week[day].date);
				setSelected({ rowId: r.id, day });
				setCollapsed((c) => ({ ...c, [r.projectId]: false }));
				setSnack({
					severity: "warning",
					text:
						missing === 1
							? "1 entry needs a memo before sending to BQE."
							: missing +
								" entries need a memo before sending to BQE.",
				});
				return;
			}
		}
		if (!keys.length || weeksRef.current[keys[0]].status !== "ready")
			return;
		setSelected(null);
		const scopes = keys
			.map((key) => {
				const wk = weeksRef.current[key];
				const days = buildWeek(key, today);
				return {
					key,
					start: days[0].iso,
					end: days[6].iso,
					cells: changedCells(wk.rows, wk.baseline, days),
				};
			})
			.filter((sc) => sc.cells.length);
		if (!scopes.length) {
			setSnack({
				severity: "info",
				text: "Nothing new to send. BQE already has this time.",
			});
			return;
		}
		sendToBqe(scopes);
	};

	// Sends straight away (a week at a time); the dialog shows progress, then
	// what BQE did with everything
	const sendToBqe = async (scopes) => {
		setPosting({ phase: "posting" });
		const results = [];
		const errors = [];
		for (const sc of scopes) {
			try {
				const res = await postAPICall(
					instance,
					accounts[0],
					"/api/time-entry/send",
					{
						start: sc.start,
						end: sc.end,
						dryRun: false,
						cells: sc.cells,
					},
				);
				results.push(...res.data.results);
				// Reload from BQE; entries BQE refused stay as unsent edits
				const failed = sc.cells.filter(
					(c, i) => res.data.results[i].status === "error",
				);
				loadWeek(sc.key, failed, { quiet: true });
			} catch (error) {
				console.error("TimeEntry - sendToBqe - error:", error);
				// A 400 is refused before anything is written (e.g. more than
				// 24 hrs on a day), so that week's edits stay as they were
				if (error.response && error.response.status === 400) {
					errors.push(
						(error.response.data.error || "Something isn't valid") +
							". That week wasn't sent.",
					);
				} else {
					// The server logs its own failures; this covers a send that
					// never got an answer (network drop, proxy timeout)
					if (!error.response || error.response.status >= 502) {
						postAPICall(instance, accounts[0], "/api/logs/automation/add", {
							employeeID: employeeID,
							tool_name: "My Time Entry - Send",
							success: 0,
							automation_log_severity_id: 4,
							message:
								`No answer from the server sending ${sc.start}..${sc.end} ` +
								`(${error.response ? "HTTP " + error.response.status : error.message}); ` +
								`${sc.cells.length} edit(s) may or may not have reached BQE:\n` +
								sc.cells
									.map(
										(c) =>
											`${c.date} | ${c.projectId} | ${c.activityId} | ${c.hours}h`,
									)
									.join("\n"),
						}).catch(() => {});
					}
					errors.push(
						"Something went wrong while sending. Reload the week to see what reached BQE.",
					);
					loadWeek(sc.key, sc.cells, { quiet: true });
				}
			}
		}
		setPosting({ phase: "done", results, error: errors.join(" ") || null });
	};

	// "Project · Activity · Day" for a line in the send dialog
	const describeResult = (r) => {
		const d = buildWeek(isoOf(mondayOf(fromIso(r.date))), today).find(
			(x) => x.iso === r.date,
		);
		return (
			projectOf(r.projectId).name +
			" · " +
			activityOf(r.activityId).name +
			" · " +
			(d ? d.long : r.date)
		);
	};

	// DERIVED VALUES
	const weekChanges = changedCells(rows, current.baseline, week);
	// Every unsent change in every loaded week (mobile sends them all)
	const allChanges = Object.keys(weeks).flatMap((key) =>
		weeks[key].status === "ready"
			? changedCells(
					weeks[key].rows,
					weeks[key].baseline,
					buildWeek(key, today),
				)
			: [],
	);
	const weekStatus = postStatus(weekChanges, current.baseline);
	// Each cell's color: not sent yet, in BQE, or locked there
	const changedKeys = changedKeySet(weekChanges);
	// Whether those changes are saved in Tools yet: null once the draft
	// matches them, else "saving" / "error" / "conflict"
	const weekDraft = drafts.current[weekKey];
	const draftNote =
		current.status !== "ready" || !weekChanges.length
			? null
			: draftSave[weekKey] === "error" || draftSave[weekKey] === "conflict"
				? draftSave[weekKey]
				: !weekDraft ||
					  weekDraft.busy ||
					  draftJsonFor(weekKey) !== weekDraft.savedJson
					? "saving"
					: null;
	const draftFailed = draftNote === "error" || draftNote === "conflict";
	const cellStateOf = (row, day) => {
		const state = cellState(
			row.cells[day],
			row.projectId,
			row.activityId,
			week[day].iso,
			changedKeys,
		);
		return state === "unsent" && draftFailed ? "unsaved" : state;
	};
	// A cell's status line, saying when its change isn't in Tools yet
	const stateTip = (state) => {
		if (!state) return "";
		if (!draftNote || !["unsent", "unsaved", "removed"].includes(state))
			return CELL_STATES[state].tip;
		return (
			(state === "removed" ? "Cleared here. " : "") + DRAFT_TIPS[draftNote]
		);
	};
	const mobileDayIso = isoOf(quickDate);
	const dayStatus = postStatus(
		weekChanges.filter((c) => c.date === mobileDayIso),
		current.baseline,
		mobileDayIso,
	);
	const dayTotals = week.map((_, i) =>
		rows.reduce((a, r) => a + toHours(r.cells[i].hours), 0),
	);
	const weekTotal = dayTotals.reduce((a, b) => a + b, 0);
	const billableTotal = rows.reduce(
		(a, r) =>
			a +
			r.cells.reduce(
				(t, c) => t + (c.billable ? toHours(c.hours) : 0),
				0,
			),
		0,
	);

	const projectOrder = [];
	rows.forEach(
		(r) =>
			!projectOrder.includes(r.projectId) &&
			projectOrder.push(r.projectId),
	);
	current.keptProjectIds.forEach(
		(p) => !projectOrder.includes(p) && projectOrder.push(p),
	);
	if (projectSort) {
		const dir = projectSort === "asc" ? 1 : -1;
		projectOrder.sort(
			(a, b) =>
				dir *
				projectOf(a).name.localeCompare(projectOf(b).name, undefined, {
					numeric: true,
					sensitivity: "base",
				}),
		);
	}
	const cycleProjectSort = () =>
		setProjectSort((s) => (s === null ? "asc" : s === "asc" ? "desc" : null));

	const stripStyle = {
		width: scrollerWidth ? scrollerWidth + "px" : "100%",
	};

	const q = addingProject ? addingProject.query.trim().toLowerCase() : "";
	const projectMatches = q
		? projects.filter((p) => matchesProject(p, q))
		: recentProjectIds.map(projectOf);

	const menuProjectId = activityMenu && activityMenu.projectId;
	const menuActivities = menuProjectId
		? activitiesFor(
				menuProjectId,
				rows
					.filter((r) => r.projectId === menuProjectId)
					.map((r) => r.activityId),
			)
		: [];

	const confirmBody = (() => {
		if (!confirm) return "";
		if (confirm.kind === "day") {
			const r = rows.find((x) => x.id === confirm.id);
			if (!r) return "";
			const d = week[confirm.day];
			return (
				activityOf(r.activityId).name +
				" on " +
				projectOf(r.projectId).name +
				" has " +
				fmtHours(toHours(r.cells[confirm.day].hours)) +
				" hrs on " +
				d.name +
				" " +
				d.short +
				". Removing it deletes this entry."
			);
		}
		if (confirm.kind === "row") {
			const r = rows.find((x) => x.id === confirm.id);
			if (!r) return "";
			return (
				activityOf(r.activityId).name +
				" on " +
				projectOf(r.projectId).name +
				" has " +
				fmtHours(rowHours(r)) +
				" hrs this week. Removing it deletes those entries."
			);
		}
		const rs = rows.filter((r) => r.projectId === confirm.id);
		const days = week
			.map((d, i) => [
				d,
				rs.reduce((a, r) => a + toHours(r.cells[i].hours), 0),
			])
			.filter(([, h]) => h > 0)
			.map(
				([d, h]) =>
					d.name + " " + d.short + " (" + fmtHours(h) + " hrs)",
			);
		const list =
			days.length > 1
				? days.slice(0, -1).join(", ") + " and " + days[days.length - 1]
				: days[0];
		return (
			projectOf(confirm.id).name +
			" has time on " +
			list +
			". Removing it deletes " +
			(rs.length === 1
				? "its activity"
				: "all " + rs.length + " activities") +
			" and those entries."
		);
	})();

	const openActivityMenu = (projectId) => (e) => {
		ensureActivities(projectId);
		setActivityMenu({ projectId, anchorEl: e.currentTarget });
	};

	// Loading / error for the page's live data (projects + this week's entries)
	const loadStatus =
		projectsState === "auth" || current.status === "auth"
			? "auth"
			: projectsState === "error" || current.status === "error"
				? "error"
				: projectsState === "ready" && current.status === "ready"
					? "ready"
					: "loading";
	const retryLoad = () => {
		if (projectsState === "error") loadBasics();
		if (current.status === "error") loadWeek(weekKey);
	};
	const loadNotice =
		loadStatus === "ready" ? null : loadStatus === "loading" ? (
			<div className="teLoadNotice">
				<CircularProgress size={20} />
				Loading your time from BQE…
			</div>
		) : loadStatus === "auth" ? (
			<Alert
				severity="warning"
				className="teLoadNotice"
				action={
					<Button
						color="inherit"
						size="small"
						onClick={() => window.location.reload()}
					>
						Reload
					</Button>
				}
			>
				Your sign-in expired. Reload the page to sign in again.
			</Alert>
		) : (
			<Alert
				severity="error"
				className="teLoadNotice"
				action={
					<Button color="inherit" size="small" onClick={retryLoad}>
						Retry
					</Button>
				}
			>
				Couldn't load your time from BQE.
			</Alert>
		);

	const draftLabel = {
		saving: "Saving draft…",
		saved: "Draft saved",
		error: "Draft not saved, retrying",
		conflict: "Draft not saved",
	}[draftSave[weekKey]];

	// "Updated 10:42 AM" + refresh button, shown once the week has loaded
	const updatedStale =
		current.loadedAt && Date.now() - current.loadedAt >= STALE_WARN_MS;
	const refreshControl = current.status === "ready" && (
		<span
			className={"teUpdated" + (updatedStale ? " teUpdated--stale" : "")}
		>
			<Tooltip
				title={
					"Refresh from BQE · Updated " +
					fmtUpdatedAt(current.loadedAt) +
					(draftLabel ? " · " + draftLabel : "")
				}
				arrow
				placement="top"
			>
				<span>
					<IconButton
						size="small"
						aria-label="Refresh from BQE"
						disabled={current.refreshing}
						onClick={() => refreshWeek(weekKey)}
					>
						{current.refreshing ? (
							<CircularProgress size={16} />
						) : (
							<RefreshIcon fontSize="small" />
						)}
					</IconButton>
				</span>
			</Tooltip>
			<span className="teUpdated-text">
				Updated {fmtUpdatedAt(current.loadedAt)}
				{draftLabel && (
					<span
						className={
							"teUpdated-draft" +
							(draftSave[weekKey] === "error" ||
							draftSave[weekKey] === "conflict"
								? " teUpdated-draft--bad"
								: "")
						}
					>
						{" · " + draftLabel}
					</span>
				)}
			</span>
		</span>
	);

	// RENDER
	return (
		<TimeEntryDataContext.Provider value={data}>
			<div
				className={"TimeEntry" + (isMobile ? " TimeEntry--mobile" : "")}
			>
				{employee && employee.debugIdentity && (
					<Alert severity="error" className="teDebugBanner">
						Debug identity: you're using this page as{" "}
						<strong>{employee.name}</strong>. Send to BQE writes
						their real time.
					</Alert>
				)}
				{isMobile ? (
					<MobileTimeEntry
						loadNotice={loadNotice}
						refreshControl={refreshControl}
						onRevert={() => revertChanges({ allWeeks: true })}
						onItMode={
							isAdmin ? () => itTimeEntryMode(mobileDayIso) : null
						}
						today={today}
						week={week}
						day={quickDate}
						onPickDate={goToDate}
						rows={rows}
						projectOrder={projectOrder}
						status={dayStatus}
						unsentCount={allChanges.length}
						hoursOnDate={hoursOnDate}
						selected={selected}
						onSelect={(rowId, day) => setSelected({ rowId, day })}
						onClose={() => setSelected(null)}
						onCell={setCell}
						onRemove={askRemove}
						onAddActivity={addActivity}
						onAddProject={landOnProject}
						onQuickAdd={openQuick}
						onSend={() => sendTime({ allWeeks: true })}
						needsMemo={needsMemo}
						cellStateOf={cellStateOf}
					/>
				) : (
					<div className="teCard">
						{/* Toolbar: title · week navigation (centered) · totals and actions */}
						<div className="teToolbar">
							<div className="teToolbar-title">
								<h1 className="teTitle">My Time Entry</h1>
								{weekStatus &&
									weekStatus !== "Not sent to BQE" && (
										<span
											className={
												"teStatus" +
												(weekStatus ===
												"Not sent to BQE"
													? ""
													: " teStatus--submitted")
											}
										>
											{weekStatus}
										</span>
									)}
								{refreshControl}
							</div>
							<div className="teToolbar-nav">
								<Tooltip
									title="Previous week"
									arrow
									placement="top"
								>
									<IconButton
										size="small"
										className="teStepBtn"
										onClick={() =>
											goToDate(addDays(week[0].date, -7))
										}
									>
										<ChevronLeftIcon fontSize="small" />
									</IconButton>
								</Tooltip>
								<Button
									className="teDateBtn"
									color="inherit"
									startIcon={
										<CalendarMonthIcon color="primary" />
									}
									endIcon={
										<ExpandMoreIcon className="teMutedIcon" />
									}
									onClick={(e) =>
										setWeekCalAnchor(e.currentTarget)
									}
								>
									{weekLabel(week)}
								</Button>
								<MonthCalendar
									anchorEl={weekCalAnchor}
									onClose={() => setWeekCalAnchor(null)}
									mode="week"
									selectedDate={week[0].date}
									today={today}
									hasHours={(d) => hoursOnDate(d) > 0}
									onPick={(d) => {
										goToDate(d);
										setWeekCalAnchor(null);
									}}
									footerLabel="This Week"
									onFooter={() => {
										goToDate(today);
										setWeekCalAnchor(null);
									}}
								/>
								<Tooltip
									title="Next week"
									arrow
									placement="top"
								>
									<IconButton
										size="small"
										className="teStepBtn"
										onClick={() =>
											goToDate(addDays(week[0].date, 7))
										}
									>
										<ChevronRightIcon fontSize="small" />
									</IconButton>
								</Tooltip>
							</div>

							<div className="teToolbar-right">
								<div className="teToolbar-actions">
									{isAdmin && (
										<Tooltip
											title="Super Secret IT Time Entry Mode"
											arrow
											placement="top"
										>
											<IconButton
												color="primary"
												aria-label="Super Secret IT Time Entry Mode"
												onClick={() =>
													itTimeEntryMode()
												}
											>
												<PestControlRodentIcon />
											</IconButton>
										</Tooltip>
									)}
									<Tooltip
										title="Shortcut: Q"
										arrow
										placement="top"
									>
										<Button
											variant="outlined"
											startIcon={<BoltIcon />}
											onClick={openQuick}
										>
											Quick Add
										</Button>
									</Tooltip>
									<Button
										variant={
											weekChanges.length
												? "contained"
												: "outlined"
										}
										disableElevation
										startIcon={<SendIcon />}
										onClick={() => sendTime()}
									>
										Send to BQE
									</Button>
								</div>
							</div>
						</div>

						{/* Reminder while the week has changes BQE doesn't have yet */}
						{weekChanges.length > 0 && (
							<div className="teUnsent" role="status">
								<span className="teUnsent-dot" />
								{weekChanges.length === 1
									? "1 change not sent to BQE"
									: weekChanges.length +
										" changes not sent to BQE"}
								<Button
									size="small"
									color="inherit"
									className="teUnsent-revert"
									startIcon={<UndoIcon fontSize="small" />}
									onClick={() => revertChanges()}
								>
									Revert changes
								</Button>
							</div>
						)}

						{/* Grid */}
						<div className="teScroller" ref={scrollerRef}>
							<div className="teGrid">
								<div className="teRow teHeadRow">
									<div className="teHeadCell teHeadCell--first">
										<TableSortLabel
											active={projectSort !== null}
											direction={projectSort || "asc"}
											onClick={cycleProjectSort}
											className="teHeadSort"
										>
											Project / Activity
										</TableSortLabel>
									</div>
									{week.map((d) => (
										<div
											key={d.iso}
											className="teHeadCell teHeadCell--day"
										>
											<span
												className={
													"teHeadDay" +
													(d.isToday
														? " teHeadDay--today"
														: d.isWeekend
															? " teHeadDay--weekend"
															: "")
												}
											>
												{d.name}
											</span>
											<span className="teHeadDate">
												{d.short}
											</span>
										</div>
									))}
									<div className="teHeadCell teHeadCell--total">
										Total
									</div>
									<div className="teRowRemove" />
								</div>

								{loadNotice && (
									<div
										className="teLoadRow"
										style={stripStyle}
									>
										{loadNotice}
									</div>
								)}

								{projectOrder.map((projectId) => {
									const project = projectOf(projectId);
									const projectRows = rows.filter(
										(r) => r.projectId === projectId,
									);
									const totals = week.map((_, i) =>
										projectRows.reduce(
											(a, r) =>
												a + toHours(r.cells[i].hours),
											0,
										),
									);
									const open = !collapsed[projectId];
									return (
										<div key={projectId}>
											<div
												ref={(el) =>
													(groupRefs.current[
														projectId
													] = el)
												}
												className={
													"teRow teProjectRow" +
													(flashProjectId ===
													projectId
														? " teProjectRow--flash"
														: "")
												}
											>
												<div className="teProjectName">
													<Tooltip
														title={
															open
																? "Collapse project"
																: "Expand project"
														}
														arrow
														placement="top"
													>
														<IconButton
															size="small"
															color="primary"
															sx={{ p: 0.25 }}
															onClick={() =>
																setCollapsed(
																	(c) => ({
																		...c,
																		[projectId]:
																			!c[
																				projectId
																			],
																	}),
																)
															}
														>
															{open ? (
																<ExpandMoreIcon fontSize="small" />
															) : (
																<ChevronRightIcon fontSize="small" />
															)}
														</IconButton>
													</Tooltip>
													<span className="teProjectName-text">
														{project.name}
													</span>
													<Tooltip
														title="Add an activity"
														arrow
														placement="top"
													>
														<IconButton
															size="small"
															color="primary"
															onClick={openActivityMenu(
																projectId,
															)}
														>
															<AddIcon fontSize="small" />
														</IconButton>
													</Tooltip>
													{hasProjectViewer &&
														project.inViewer && (
															<Tooltip
																title="Open in Project Viewer"
																arrow
																placement="top"
															>
																<IconButton
																	size="small"
																	className="teRowLink"
																	href={projectViewerUrl(
																		projectId,
																	)}
																	target="_blank"
																	rel="noopener noreferrer"
																	aria-label="Open in Project Viewer"
																>
																	<VisibilityIcon fontSize="small" />
																</IconButton>
															</Tooltip>
														)}
													<Tooltip
														title="Open in BQE"
														arrow
														placement="top"
													>
														<IconButton
															size="small"
															className="teRowLink"
															href={bqeProjectUrl(
																projectId,
															)}
															target="_blank"
															rel="noopener noreferrer"
															aria-label="Open in BQE"
														>
															<OpenInNewIcon fontSize="small" />
														</IconButton>
													</Tooltip>
												</div>
												{totals.map((t, i) => (
													<div
														key={i}
														className={
															"teProjectDayTotal" +
															(t
																? ""
																: " teProjectDayTotal--none")
														}
													>
														{fmtHours(t)}
													</div>
												))}
												<div className="teProjectTotal">
													{fmtHours(
														totals.reduce(
															(a, b) => a + b,
															0,
														),
													)}
												</div>
												<div className="teRowRemove">
													<Tooltip
														title="Remove project"
														arrow
														placement="top"
													>
														<IconButton
															size="small"
															className="teDeleteBtn"
															onClick={() =>
																askRemove(
																	"project",
																	projectId,
																)
															}
														>
															<DeleteOutlineIcon fontSize="small" />
														</IconButton>
													</Tooltip>
												</div>
											</div>

											{open &&
												projectRows.length === 0 && (
													<div
														className="teEmptyRow"
														style={stripStyle}
													>
														<span>
															No activities yet.
														</span>
														<Button
															size="small"
															onClick={openActivityMenu(
																projectId,
															)}
														>
															Add an activity
														</Button>
													</div>
												)}

											{open &&
												projectRows.map((row) => (
													<ActivityRow
														key={row.id}
														row={row}
														week={week}
														states={row.cells.map(
															(_, i) =>
																cellStateOf(
																	row,
																	i,
																),
														)}
														stateTip={stateTip}
														selectedDay={
															selected &&
															selected.rowId ===
																row.id
																? selected.day
																: null
														}
														stripStyle={stripStyle}
														onSelect={(day) =>
															setSelected({
																rowId: row.id,
																day,
															})
														}
														onClose={() =>
															setSelected(null)
														}
														onCell={(day, patch) =>
															setCell(
																row.id,
																day,
																patch,
															)
														}
														onRemove={() =>
															askRemove(
																"row",
																row.id,
															)
														}
													/>
												))}
										</div>
									);
								})}

								{loadStatus === "ready" &&
									projectOrder.length === 0 &&
									!addingProject && (
										<div
											className="teEmptyWeek"
											style={stripStyle}
										>
											<span>
												No time on this week yet.
											</span>
											<Button
												size="small"
												onClick={() =>
													setAddingProject({
														query: "",
													})
												}
											>
												Add a project
											</Button>
											<span>or press</span>
											<kbd className="teKbd">Q</kbd>
											<span>for Quick Add.</span>
										</div>
									)}

								{/* Bottom of the grid, pinned: "Add a project" (or its search)
    sits just above the daily totals */}
								<div className="teGridBottom">
									{addingProject ? (
										<AddProjectStrip
											style={stripStyle}
											query={addingProject.query}
											label={
												q ? "Matches" : "Recent for you"
											}
											matches={projectMatches.slice(0, 8)}
											noMatches={
												q.length > 0 &&
												projectMatches.length === 0
											}
											onQuery={(query) =>
												setAddingProject({ query })
											}
											onPick={landOnProject}
											onCancel={() =>
												setAddingProject(null)
											}
										/>
									) : (
										<div
											className="teAddBar"
											style={stripStyle}
										>
											<Button
												variant="outlined"
												size="small"
												startIcon={<AddIcon />}
												onClick={() =>
													setAddingProject({
														query: "",
													})
												}
											>
												Add a project
											</Button>
										</div>
									)}

									<div className="teRow teTotalsRow">
										<div className="teTotalsLabel">
											<span className="teTotalsLabel-main">
												Daily totals
											</span>
											<span className="teTotalsStats">
												<span className="teTotalsStat">
													<span className="teTotalsStat-label">
														Billable
													</span>
													{fmtNumber(billableTotal)}
												</span>
												<span className="teTotalsStat">
													<span className="teTotalsStat-label">
														Non-billable
													</span>
													{fmtNumber(
														weekTotal -
															billableTotal,
													)}
												</span>
											</span>
										</div>
										{week.map((d, i) => (
											<div
												key={d.iso}
												className={
													"teDayTotal" +
													(dayTotals[i]
														? ""
														: " teDayTotal--none")
												}
											>
												{fmtHours(dayTotals[i])}
											</div>
										))}
										<div className="teWeekTotal">
											{fmtNumber(weekTotal)}
											<span className="teWeekTotal-target">
												/ {fmtNumber(weeklyTarget)}
											</span>
										</div>
										<div className="teRowRemove" />
									</div>
								</div>
							</div>
						</div>

						{/* Footer */}
						<div className="teFooter">
							<span className="teFooter-hint">
								Use + on a project to add an activity.
							</span>
							{CELL_LEGEND.map((key) => (
								<Tooltip
									key={key}
									title={CELL_STATES[key].tip}
									arrow
									placement="top"
								>
									<span className="teLegend">
										<span
											className={
												"teLegend-swatch teLegend-swatch--" +
												key
											}
										/>
										{CELL_STATES[key].label}
									</span>
								</Tooltip>
							))}
							<span className="teLegend">
								<TextSnippetIcon className="teLegend-icon" />
								has a memo
							</span>
							<span className="teLegend">
								<TextSnippetIcon className="teLegend-icon teLegend-icon--needed" />
								memo still needed
							</span>
						</div>
					</div>
				)}

				{/* Add-activity menu */}
				<Menu
					anchorEl={activityMenu && activityMenu.anchorEl}
					open={Boolean(activityMenu)}
					onClose={() => setActivityMenu(null)}
					PaperProps={{ className: "teActMenu" }}
					MenuListProps={{ dense: true, disablePadding: true }}
				>
					<div className="teActMenu-head">Add an activity</div>
					{menuActivities.map((a) => (
						<MenuItem
							key={a.id}
							className="teActMenu-item"
							onClick={() => addActivity(menuProjectId, a.id)}
						>
							<span className="teOption">
								<span
									className={
										"teOption-name" +
										(a.id ===
										projectOf(menuProjectId)
											.mostUsedActivityId
											? " teOption-name--blue"
											: "")
									}
								>
									{a.name}
								</span>
								<span className="teOption-sub">
									{a.description}
								</span>
							</span>
						</MenuItem>
					))}
					{menuProjectId && menuActivities.length === 0 && (
						<div className="teActMenu-empty">
							{activitiesState(menuProjectId) === "ready"
								? "Every activity is already on this project"
								: activitiesState(menuProjectId) === "error"
									? "Couldn't load activities. Close and try again."
									: "Loading activities…"}
						</div>
					)}
				</Menu>

				{/* Remove confirmation */}
				<Dialog
					open={Boolean(confirm)}
					onClose={() => setConfirm(null)}
					fullWidth
					maxWidth="xs"
				>
					<DialogTitle className="teDialogTitle">
						{confirm && confirm.kind !== "project"
							? "Remove Activity"
							: "Remove Project"}
					</DialogTitle>
					<DialogContent className="teDialogContent">
						<p className="teDialogText">{confirmBody}</p>
						<div className="dialogButtons">
							<Button
								variant="outlined"
								color="error"
								onClick={() => setConfirm(null)}
							>
								Cancel
							</Button>
							<Button
								variant="contained"
								color="error"
								disableElevation
								onClick={() =>
									confirm.kind === "day"
										? removeDayEntry(
												confirm.id,
												confirm.day,
											)
										: confirm.kind === "row"
											? removeRow(confirm.id)
											: removeProject(confirm.id)
								}
							>
								Remove
							</Button>
						</div>
					</DialogContent>
				</Dialog>

				{/* Send to BQE: progress, then what BQE did with the changes */}
				<Dialog
					open={Boolean(posting)}
					onClose={() =>
						posting &&
						posting.phase !== "posting" &&
						setPosting(null)
					}
					fullWidth
					maxWidth="sm"
				>
					<DialogTitle className="teDialogTitle">
						Send to BQE
					</DialogTitle>
					<DialogContent className="teDialogContent">
						{posting && (
							<SendDialogBody
								posting={posting}
								describe={describeResult}
								onClose={() => setPosting(null)}
							/>
						)}
					</DialogContent>
				</Dialog>

				<QuickAddDialog
					mobile={isMobile}
					open={quickOpen}
					onClose={() => setQuickOpen(false)}
					week={week}
					quickDate={quickDate}
					onPickDate={goToDate}
					today={today}
					hoursOnDate={hoursOnDate}
					projectHoursOnDate={projectHoursOnDate}
					dailyTarget={weeklyTarget / 5}
					existingCell={existingCell}
					suggestions={suggestions}
					onSave={quickSave}
				/>

				<Snackbar
					open={Boolean(snack)}
					autoHideDuration={snack && snack.actions ? null : 4000}
					onClose={() => setSnack(null)}
					anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				>
					{snack ? (
						<Alert
							severity={snack.severity}
							variant="filled"
							className="teSnack"
							onClose={() => setSnack(null)}
							action={
								snack.actions && (
									<>
										{snack.actions.map((a) => (
											<Button
												key={a.label}
												color="inherit"
												size="small"
												onClick={() => {
													setSnack(null);
													a.onClick();
												}}
											>
												{a.label}
											</Button>
										))}
									</>
								)
							}
						>
							{snack.text}
						</Alert>
					) : (
						<span />
					)}
				</Snackbar>
			</div>
		</TimeEntryDataContext.Provider>
	);
}

// One activity line: seven day cells, plus the memo/flags strip for the selected day
function ActivityRow({
	row,
	week,
	states,
	stateTip,
	selectedDay,
	stripStyle,
	onSelect,
	onClose,
	onCell,
	onRemove,
}) {
	const { activityOf } = useTimeEntryData();
	const activity = activityOf(row.activityId);
	const open = selectedDay != null ? row.cells[selectedDay] : null;
	const memoMissing = open && needsMemo(open);

	return (
		<div className="teActivity">
			<div className="teRow teActivityRow">
				<div className="teActivityName">
					<span className="teActivityName-tick" />
					<span className="teActivityName-text">
						<span className="teActivityName-name">
							{activity.name}
						</span>
						<span className="teActivityName-desc">
							{activity.description}
						</span>
					</span>
				</div>
				{row.cells.map((c, i) => {
					const has = toHours(c.hours) > 0;
					const locked = Boolean(c.lockReason);
					const state = states[i];
					const cls = [
						"teCell",
						selectedDay === i && "teCell--selected",
						has && "teCell--has",
						week[i].isWeekend && "teCell--weekend",
						locked && "teCell--locked",
						state && "teCell--" + state,
					]
						.filter(Boolean)
						.join(" ");
					const memoNeeded = needsMemo(c);
					// Status first, then the memo (or that one is needed)
					const tip = locked
						? c.lockReason + " in BQE, so it can't be edited here"
						: [
								stateTip(state),
								memoNeeded
									? "Memo needed. Click to add one."
									: has
										? c.memo
										: "",
							]
								.filter(Boolean)
								.join("\n");
					return (
						<Tooltip
							key={i}
							title={
								tip && <span className="teCellTip">{tip}</span>
							}
							arrow
							placement="top"
							enterDelay={300}
						>
							<div className="teCellWrap">
								<input
									data-te-cell={row.id + "-" + i}
									className={cls}
									value={c.hours}
									placeholder="—"
									inputMode="decimal"
									readOnly={locked}
									aria-label={
										activity.name + " · " + week[i].long
									}
									onFocus={() => onSelect(i)}
									onKeyDown={(e) => {
										if (e.key === "Escape") {
											e.currentTarget.blur();
											onClose();
										}
									}}
									onChange={(e) =>
										!locked &&
										hoursInputOk(e.target.value, c.hours) &&
										onCell(i, { hours: e.target.value })
									}
								/>
								{has && (
									<TextSnippetIcon
										className={
											"teCellMemo" +
											(memoNeeded
												? " teCellMemo--needed"
												: "")
										}
									/>
								)}
							</div>
						</Tooltip>
					);
				})}
				<div className="teActivityTotal">{fmtHours(rowHours(row))}</div>
				<div className="teRowRemove">
					<Tooltip title="Remove row" arrow placement="top">
						<IconButton
							size="small"
							className="teDeleteBtn"
							onClick={onRemove}
						>
							<CloseIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</div>
			</div>

			{open && (
				<div className="teDetail" style={stripStyle}>
					<TextField
						className="teDetail-memo"
						disabled={Boolean(open.lockReason)}
						multiline
						rows={2}
						size="small"
						label={"Memo · " + week[selectedDay].long}
						value={open.memo}
						placeholder="What was done, in your own words…"
						error={Boolean(memoMissing)}
						helperText={memoMissing ? "Memo is required" : " "}
						onChange={(e) =>
							onCell(selectedDay, { memo: e.target.value })
						}
						onKeyDown={(e) => {
							if (
								e.key === "Escape" ||
								(e.key === "Enter" && !e.shiftKey)
							) {
								e.preventDefault();
								onClose();
							}
						}}
					/>
					<FlagCheckboxes
						small
						className="teDetail-flags"
						values={open}
						disabled={Boolean(open.lockReason)}
						onToggle={(key) =>
							onCell(selectedDay, { [key]: !open[key] })
						}
					/>
					<div className="teDetail-actions">
						<Button
							size="small"
							variant="outlined"
							color="error"
							startIcon={<DeleteOutlineIcon />}
							disabled={Boolean(open.lockReason)}
							onClick={() => {
								onCell(selectedDay, { hours: "", memo: "" });
								onClose();
							}}
						>
							Clear
						</Button>
						<Button
							size="small"
							variant="contained"
							disableElevation
							onClick={onClose}
						>
							Done
						</Button>
					</div>
				</div>
			)}
		</div>
	);
}

// Inline "Add a project" search, docked under the last project
function AddProjectStrip({
	style,
	query,
	label,
	matches,
	noMatches,
	onQuery,
	onPick,
	onCancel,
}) {
	// The list renders in a Popper (portal) so the grid's scroller can't clip
	// it; it opens downward and flips above when there's no room below.
	const [anchorEl, setAnchorEl] = useState(null);

	return (
		<div className="teAddStrip" style={style}>
			<span className="teAddStrip-label">
				<CreateNewFolderIcon fontSize="small" />
				Add a project
			</span>
			<div className="teAddStrip-search" ref={setAnchorEl}>
				<TextField
					autoFocus
					fullWidth
					size="small"
					value={query}
					placeholder="Search projects…"
					onChange={(e) => onQuery(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Escape") onCancel();
						if (e.key === "Enter" && matches.length)
							onPick(matches[0].id);
					}}
				/>
				<Popper
					open={Boolean(anchorEl)}
					anchorEl={anchorEl}
					placement="bottom-start"
					className="teAddStrip-popper"
					style={{
						width: anchorEl ? anchorEl.offsetWidth : undefined,
					}}
					modifiers={[
						{ name: "offset", options: { offset: [0, 4] } },
					]}
				>
					<div className="teAddStrip-list">
						<div className="teAddStrip-listHead">{label}</div>
						{matches.map((p) => (
							<button
								type="button"
								key={p.id}
								className="teAddStrip-option"
								onClick={() => onPick(p.id)}
							>
								<span className="teOption-name">{p.name}</span>
							</button>
						))}
						{noMatches && (
							<div className="teAddStrip-none">
								No Results Found
							</div>
						)}
					</div>
				</Popper>
			</div>
			<span className="teAddStrip-hint">
				Pick a project to add it to the week, then use + to choose its
				activities.
			</span>
			<Button size="small" variant="outlined" onClick={onCancel}>
				Cancel
			</Button>
		</div>
	);
}
